import ExcelJS from "exceljs";

/**
 * Fill COMMON ASP template with ExcelJS so borders, logo, and number formats are preserved.
 * 1) Label-based smart fill (always) — maps Load board values into Chemplast COMMON form layout
 * 2) Optional ASP mapping profile (cellRef / header) applied on top when provided
 */

function parseExcelCellRef(ref) {
  const raw = String(ref || "")
    .trim()
    .replace(/\$/g, "")
    .toUpperCase();
  const m = /^([A-Z]+)(\d+)$/.exec(raw);
  if (!m) return null;
  let col0 = 0;
  for (let i = 0; i < m[1].length; i += 1) {
    col0 = col0 * 26 + (m[1].charCodeAt(i) - 64);
  }
  return { row1: Number(m[2]), col0: col0 - 1 };
}

function normalizeHeader(h) {
  return String(h || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function normalizeColumnMapEntry(raw) {
  if (raw == null || raw === "") return null;
  if (typeof raw === "string") return { header: raw.trim(), cellRef: "" };
  if (typeof raw === "object") {
    return {
      header: raw.header != null ? String(raw.header).trim() : "",
      cellRef: raw.cellRef != null ? String(raw.cellRef).trim().replace(/\$/g, "").toUpperCase() : "",
    };
  }
  return null;
}

function isEmptyVal(v) {
  return v === undefined || v === null || v === "";
}

function getScopeForKey(key, profile) {
  const overrides = profile?.fieldScopeOverrides || {};
  if (overrides[key] === "line" || overrides[key] === "shipment") return overrides[key];
  const spvp = typeof window !== "undefined" ? window.SPVP_ASP : null;
  if (spvp?.getScopeForKey) return spvp.getScopeForKey(key, profile);
  return "line";
}

function getMappingAnchors(profile) {
  let hRef = profile.headerCellRef;
  let fRef = profile.firstLineCellRef;
  if (!hRef && profile.columnHeaderRow != null && profile.columnHeaderRow !== "") {
    hRef = `A${profile.columnHeaderRow}`;
  }
  if (!fRef && profile.firstLineItemRow != null && profile.firstLineItemRow !== "") {
    fRef = `A${profile.firstLineItemRow}`;
  }
  if (!hRef) hRef = "A1";
  if (!fRef) fRef = "A2";
  const hParsed = parseExcelCellRef(hRef);
  const fParsed = parseExcelCellRef(fRef);
  if (!hParsed) throw new Error(`Invalid header cell reference: ${hRef}`);
  if (!fParsed) throw new Error(`Invalid first line-item cell reference: ${fRef}`);
  return {
    headerRow1: hParsed.row1,
    headerStartCol0: hParsed.col0,
    lineStartRow1: fParsed.row1,
    lineStartCol0: fParsed.col0,
  };
}

function col0ToLetters(col0) {
  let n = col0 + 1;
  let s = "";
  while (n > 0) {
    const rem = (n - 1) % 26;
    s = String.fromCharCode(65 + rem) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
}

function cellDisplayText(value) {
  if (value == null) return "";
  if (typeof value === "object") {
    if (value.text != null) return String(value.text);
    if (Array.isArray(value.richText)) return value.richText.map((t) => t.text || "").join("");
    if (value.result != null) return String(value.result);
    if (value.sharedFormula != null || value.formula != null) return "";
  }
  return String(value);
}

function setCellValuePreserveStyle(sheet, row1, col1OrCol0, value, colIsOneBased = true) {
  const col1 = colIsOneBased ? col1OrCol0 : col1OrCol0 + 1;
  const cell = sheet.getCell(row1, col1);
  if (value === "" || value == null) {
    cell.value = null;
    return;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    // Large integer IDs must stay exact — write as text (Excel scientific notation loses digits).
    if (Number.isInteger(value) && Math.abs(value) >= 1e10) {
      cell.numFmt = "@";
      cell.value = String(Math.trunc(value));
      return;
    }
    cell.value = value;
    return;
  }
  if (value instanceof Date) {
    cell.value = value;
    return;
  }
  const str = String(value).trim();
  // ShipmentReleaseId / Shipment # etc. — keep as text so Excel shows 202607090730 not 2.02607E+11
  if (/^\d{10,}$/.test(str)) {
    cell.numFmt = "@";
    cell.value = str;
    return;
  }
  cell.value = String(value);
}

function readHeaderRow(sheet, headerRow1, startCol0) {
  const headers = [];
  const row = sheet.getRow(headerRow1);
  const maxCol = Math.max(row.cellCount || 0, startCol0 + 40);
  for (let c = startCol0; c < maxCol; c += 1) {
    headers.push(cellDisplayText(row.getCell(c + 1).value).trim());
  }
  return headers;
}

function resolveColIndex(headers, headerName) {
  const want = normalizeHeader(headerName);
  if (!want) return -1;
  for (let i = 0; i < headers.length; i += 1) {
    if (normalizeHeader(headers[i]) === want) return i;
  }
  // partial contains match
  for (let i = 0; i < headers.length; i += 1) {
    const h = normalizeHeader(headers[i]);
    if (h && (h.includes(want) || want.includes(h))) return i;
  }
  return -1;
}

function findLabelCell(sheet, regex) {
  let found = null;
  sheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
    if (found) return;
    row.eachCell({ includeEmpty: false }, (cell, colNumber) => {
      if (found) return;
      const text = cellDisplayText(cell.value);
      if (regex.test(text)) found = { row: rowNumber, col: colNumber, text };
    });
  });
  return found;
}

function setRightOfLabel(sheet, regex, value, colOffset = 1) {
  if (isEmptyVal(value) && value !== 0) return false;
  const loc = findLabelCell(sheet, regex);
  if (!loc) return false;
  setCellValuePreserveStyle(sheet, loc.row, loc.col + colOffset, value, true);
  return true;
}

/**
 * Chemplast COMMON ASP form: find labels and write Load board values beside them / into the line table.
 */
function applySmartCommonAspFill(sheet, shipmentOnly, lineRows) {
  const s = shipmentOnly || {};
  const rows = Array.isArray(lineRows) ? lineRows : [];

  // Header / shipment fields (label → cell to the right)
  setRightOfLabel(sheet, /^creation\s*date$/i, s.creationDate);
  setRightOfLabel(sheet, /^created\s*by$/i, s.createdBy);
  setRightOfLabel(sheet, /^shipment\s*ready\s*date$/i, s.shipmentReadyDate);
  setRightOfLabel(sheet, /^shipment\s*ready\s*time$/i, s.shipmentReadyTime);
  setRightOfLabel(sheet, /shipping\s*dock\s*hours/i, s.shippingDockHours);
  setRightOfLabel(sheet, /^plan\s*delivery\s*date$/i, s.planDeliveryDate);
  setRightOfLabel(sheet, /^plan\s*delivery\s*time$/i, s.planDeliveryTime);
  setRightOfLabel(sheet, /^shipment\s*#?\s*$/i, s.shipmentNumber, 1) ||
    setRightOfLabel(sheet, /shipment\s*#/i, s.shipmentNumber, 1);
  setRightOfLabel(sheet, /^rev\s*#?\s*$/i, s.rev != null ? s.rev : undefined);
  setRightOfLabel(sheet, /carrier\s*name/i, s.carrierName);
  setRightOfLabel(sheet, /carrier\s*contact/i, s.carrierContact);
  setRightOfLabel(sheet, /carrier\s*phone/i, s.carrierPhone);
  setRightOfLabel(sheet, /total\s*boxes/i, s.totalBoxes);
  setRightOfLabel(sheet, /total\s*pallets/i, s.totalPallets);
  setRightOfLabel(sheet, /total\s*weight/i, s.totalWeight);

  // Ship-To block: write lines under / beside "Ship To"
  const shipToLabel = findLabelCell(sheet, /^ship\s*to$/i) || findLabelCell(sheet, /ship\s*to\s*information/i);
  if (shipToLabel) {
    const baseCol = shipToLabel.col + ( /^ship\s*to$/i.test(shipToLabel.text) ? 1 : 3);
    const startRow = /^ship\s*to$/i.test(shipToLabel.text) ? shipToLabel.row : shipToLabel.row + 1;
    if (!isEmptyVal(s.shipToLine1)) setCellValuePreserveStyle(sheet, startRow, baseCol, s.shipToLine1, true);
    if (!isEmptyVal(s.shipToLine2)) setCellValuePreserveStyle(sheet, startRow + 1, baseCol, s.shipToLine2, true);
    if (!isEmptyVal(s.shipToLine3)) setCellValuePreserveStyle(sheet, startRow + 2, baseCol, s.shipToLine3, true);
  }

  // Line-item table: find header row that contains Part Number
  let headerRow = null;
  let headerCols = {};
  sheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
    if (headerRow) return;
    const texts = [];
    row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      const t = normalizeHeader(cellDisplayText(cell.value));
      if (t) texts.push({ col: colNumber, t });
    });
    const joined = texts.map((x) => x.t).join(" | ");
    if (joined.includes("part number") || (joined.includes("part") && joined.includes("description") && joined.includes("po"))) {
      headerRow = rowNumber;
      texts.forEach(({ col, t }) => {
        if (t.includes("line") && (t.includes("#") || t === "line #")) headerCols.lineNumber = col;
        else if (t === "part number" || t === "part #") headerCols.partNumber = col;
        else if (t.includes("description")) headerCols.partDescription = col;
        else if (t.includes("po")) headerCols.poNumber = col;
        else if (t.includes("number of box") || t === "boxes") headerCols.numberOfBoxes = col;
        else if (t.includes("qty per box") || t.includes("qty/box")) headerCols.qtyPerBox = col;
        else if (t.includes("pallet")) headerCols.palletsQty = col;
        else if (t.includes("planned ship")) headerCols.plannedShipQuantity = col;
        else if (t === "uom") headerCols.uom = col;
        else if (t.includes("mixed")) headerCols.mixedPalletYn = col;
        else if (t.includes("dimension")) headerCols.dimensionsLWH = col;
        else if (t.includes("weight")) headerCols.totalShipWeight = col;
        else if (t.includes("cube")) headerCols.totalCube = col;
        else if (t.includes("delete")) headerCols.deleteYn = col;
        else if (t.includes("release")) headerCols.shipmentReleaseId = col;
      });
    }
  });

  if (headerRow && headerCols.partNumber) {
    const firstDataRow = headerRow + 1;
    // Clear a few sample rows then write real lines
    for (let r = 0; r < Math.max(rows.length, 8); r += 1) {
      const excelRow = firstDataRow + r;
      Object.values(headerCols).forEach((col) => {
        setCellValuePreserveStyle(sheet, excelRow, col, null, true);
      });
    }
    rows.forEach((line, idx) => {
      const excelRow = firstDataRow + idx;
      const write = (key, val) => {
        if (!headerCols[key]) return;
        if (isEmptyVal(val) && val !== 0) return;
        setCellValuePreserveStyle(sheet, excelRow, headerCols[key], val, true);
      };
      write("lineNumber", line.lineNumber || String(idx + 1).padStart(2, "0"));
      write("partNumber", line.partNumber);
      write("partDescription", line.partDescription);
      write("poNumber", line.poNumber);
      write("numberOfBoxes", line.numberOfBoxes);
      write("qtyPerBox", line.qtyPerBox);
      write("palletsQty", line.palletsQty);
      write("plannedShipQuantity", line.plannedShipQuantity);
      write("uom", line.uom || "EA");
      write("mixedPalletYn", line.mixedPalletYn || "N");
      write("dimensionsLWH", line.dimensionsLWH);
      write("totalShipWeight", line.totalShipWeight);
      write("totalCube", line.totalCube);
      write("deleteYn", line.deleteYn);
      write("shipmentReleaseId", line.shipmentReleaseId);
    });
  }
}

function applyMappingProfileFill(sheet, profile, lineRows, shipmentOnly) {
  if (!profile || !profile.columnMap || !Object.keys(profile.columnMap).length) return;

  const anchors = getMappingAnchors(profile);
  const headers = readHeaderRow(sheet, anchors.headerRow1, anchors.headerStartCol0);
  const colMap = profile.columnMap || {};
  const rows = Array.isArray(lineRows) ? lineRows : [];

  function writeKeyValue(key, value, lineIndex) {
    const entry = normalizeColumnMapEntry(colMap[key]);
    if (!entry) return;
    const hasHeader = !isEmptyVal(entry.header);
    const hasCell = !isEmptyVal(entry.cellRef);
    if (!hasHeader && !hasCell) return;

    const scope = getScopeForKey(key, profile);

    if (hasCell) {
      const p = parseExcelCellRef(entry.cellRef);
      if (!p) return;
      let row1 = p.row1;
      if (scope === "line") row1 += lineIndex;
      setCellValuePreserveStyle(sheet, row1, p.col0, value, false);
      return;
    }

    const idxInSlice = resolveColIndex(headers, entry.header);
    if (idxInSlice < 0) return;
    const colAbs = anchors.headerStartCol0 + idxInSlice;
    let row1 = anchors.lineStartRow1;
    if (scope === "line") row1 += lineIndex;
    if (scope === "shipment" && lineIndex > 0) return;
    setCellValuePreserveStyle(sheet, row1, colAbs, value, false);
  }

  for (let i = 0; i < Math.max(rows.length, 1); i += 1) {
    const data = { ...shipmentOnly, ...(rows[i] || {}) };
    Object.keys(colMap).forEach((key) => {
      if (isEmptyVal(data[key]) && data[key] !== 0) return;
      const scope = getScopeForKey(key, profile);
      if (scope === "shipment") {
        if (i === 0) writeKeyValue(key, data[key], i);
      } else if (rows.length) {
        writeKeyValue(key, data[key], i);
      }
    });
  }
}

/**
 * @param {Uint8Array|ArrayBuffer} templateBytes
 * @param {object|null} profile optional ASP mapping profile
 * @param {object[]} lineRows
 * @param {object} shipmentOnly
 * @returns {Promise<Uint8Array>}
 */
export async function fillAspTemplatePreservingStyles(templateBytes, profile, lineRows, shipmentOnly = {}) {
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(templateBytes);

  const sheetName = profile?.sheetName || workbook.worksheets[0]?.name;
  const sheet = workbook.getWorksheet(sheetName);
  if (!sheet) throw new Error(`Worksheet not found: ${sheetName}`);

  // Always map Load board → COMMON form by labels/headers first (fixes sample Poly-Coat data).
  applySmartCommonAspFill(sheet, shipmentOnly, lineRows);

  // Optional saved ASP mapping overrides specific cells.
  if (profile) {
    applyMappingProfileFill(sheet, profile, lineRows, shipmentOnly);
  }

  const out = await workbook.xlsx.writeBuffer();
  return new Uint8Array(out);
}

export function uint8ArrayToBase64(bytes) {
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}
